import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-scroll';
import { Link as RouterLink } from 'react-router-dom';
import { FiArrowRight, FiTerminal, FiShield, FiZap } from 'react-icons/fi';
import anime from 'animejs';
import ShapeGrid from '../fx/ShapeGrid';
import FloatingShapes from '../fx/FloatingShapes';
import SplitText from '../fx/SplitText';
import Magnetic from '../fx/Magnetic';
import ClickBurst from '../fx/ClickBurst';
import './Hero.css';

const rotatingWords = ['Train.', 'Defend.', 'Compete.', 'Outsmart.'];

const terminalLines = [
    { text: '$ ./siberlab --scan corp.target.lab', className: 'text-emerald-400' },
    { text: '[*] Enumerating attack surface...', className: 'text-gray-300' },
    { text: '[+] 24 endpoints discovered', className: 'text-gray-300' },
    { text: '[!] CVE-2024-XXXXX exploitable', className: 'text-yellow-300' },
    { text: '[!] CRITICAL: stored XSS chain to ATO', className: 'text-red-400' },
    { text: '[#] Recommendation:', className: 'text-gray-300 mt-2' },
    { text: '    book a SiberLab red team engagement', className: 'text-red-400 font-semibold' },
];

const Hero = () => {
    const [currentLine, setCurrentLine] = useState(0);
    const [charIndex, setCharIndex] = useState(0);
    const [linesToShow, setLinesToShow] = useState([]);
    const [rotIdx, setRotIdx] = useState(0);
    const titleRef = useRef(null);
    const subRef = useRef(null);
    const heroRef = useRef(null);
    const rotateRef = useRef(null);
    const ctaRef = useRef(null);
    const statsRef = useRef(null);
    const termRef = useRef(null);

    // Choreographed entrance
    useEffect(() => {
        const tl = anime.timeline({ easing: 'easeOutExpo' });

        // Title letters: drop in from above with rotation, spring settle
        if (titleRef.current) {
            const chars = titleRef.current.querySelectorAll('.char');
            tl.add({
                targets: chars,
                opacity: [0, 1],
                translateY: [-80, 0],
                rotateZ: [-30, 0],
                scale: [0.5, 1],
                duration: 1100,
                delay: anime.stagger(28, { from: 'first' }),
                easing: 'easeOutElastic(1, .6)',
            }, 200);
        }
        // Sub text fade up
        if (subRef.current) {
            tl.add({
                targets: subRef.current.querySelectorAll('.char'),
                opacity: [0, 1],
                translateY: [16, 0],
                duration: 700,
                delay: anime.stagger(18),
                easing: 'easeOutCubic',
            }, '-=600');
        }
        // CTAs scale in
        if (ctaRef.current) {
            tl.add({
                targets: ctaRef.current.querySelectorAll('.cta-item'),
                opacity: [0, 1],
                translateY: [20, 0],
                scale: [0.92, 1],
                duration: 700,
                delay: anime.stagger(90),
                easing: 'easeOutBack',
            }, '-=400');
        }
        // Stats cascade
        if (statsRef.current) {
            tl.add({
                targets: statsRef.current.querySelectorAll('.stat'),
                opacity: [0, 1],
                translateY: [30, 0],
                rotateX: [-25, 0],
                duration: 800,
                delay: anime.stagger(120),
            }, '-=400');
        }
        // Terminal slide in
        if (termRef.current) {
            tl.add({
                targets: termRef.current,
                opacity: [0, 1],
                translateX: [60, 0],
                duration: 900,
            }, '-=1000');
        }

        // Continuous title breathing once entrance settles
        const breathTimer = setTimeout(() => {
            if (!titleRef.current) return;
            anime({
                targets: titleRef.current.querySelectorAll('.char'),
                translateY: [0, -4, 0],
                duration: 4200,
                loop: true,
                delay: anime.stagger(40),
                easing: 'easeInOutSine',
            });
        }, 2200);
        return () => clearTimeout(breathTimer);
    }, []);

    // Rotating word with anime morph
    useEffect(() => {
        const t = setInterval(() => {
            if (rotateRef.current) {
                anime({
                    targets: rotateRef.current,
                    opacity: [1, 0],
                    translateY: [0, -20],
                    rotateX: [0, -90],
                    duration: 350,
                    easing: 'easeInCubic',
                    complete: () => {
                        setRotIdx((i) => (i + 1) % rotatingWords.length);
                        anime({
                            targets: rotateRef.current,
                            opacity: [0, 1],
                            translateY: [20, 0],
                            rotateX: [90, 0],
                            duration: 500,
                            easing: 'easeOutBack',
                        });
                    },
                });
            }
        }, 2400);
        return () => clearInterval(t);
    }, []);

    // Terminal typewriter
    useEffect(() => {
        if (currentLine < terminalLines.length) {
            const lineText = terminalLines[currentLine].text;
            if (charIndex < lineText.length) {
                const t = setTimeout(() => setCharIndex((p) => p + 1), 28);
                return () => clearTimeout(t);
            } else {
                const t = setTimeout(() => {
                    setLinesToShow((prev) => [...prev, lineText]);
                    setCurrentLine((p) => p + 1);
                    setCharIndex(0);
                }, 380);
                return () => clearTimeout(t);
            }
        } else {
            const t = setTimeout(() => {
                setCurrentLine(0);
                setCharIndex(0);
                setLinesToShow([]);
            }, 5400);
            return () => clearTimeout(t);
        }
    }, [charIndex, currentLine]);

    return (
        <section
            id="home"
            ref={heroRef}
            className="relative bg-[#05060a] text-white overflow-hidden"
            style={{ perspective: '1200px' }}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-grid opacity-40" aria-hidden="true" />
            <div className="absolute inset-0 spotlight" aria-hidden="true" />
            <ShapeGrid density={42} />
            <FloatingShapes />
            <ClickBurst targetRef={heroRef} />
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#05060a] z-10" aria-hidden="true" />

            <div className="relative z-20 px-6 md:px-16 pt-20 pb-28 min-h-[94vh] flex flex-col justify-center max-w-7xl mx-auto">
                <div className="flex items-center gap-3 mb-8">
                    <span className="pill tag"><span className="pulse-dot" /> Active engagements 2026</span>
                    <span className="hidden md:inline text-xs text-gray-400 font-mono">v4.7 / akademi · redteam · ctf-ops</span>
                </div>

                <div className="grid lg:grid-cols-12 gap-12 items-start">
                    {/* Left */}
                    <div className="lg:col-span-7">
                        <h1 ref={titleRef} className="font-display text-5xl md:text-7xl lg:text-[5.4rem] font-bold leading-[1.02] mb-6">
                            <span className="block"><SplitText text="Secure your" /></span>
                            <span className="block"><SplitText text="digital future." /></span>
                            <span className="block mt-3 text-gradient-red">
                                <SplitText text="SiberLab ID" />
                            </span>
                        </h1>

                        <div ref={subRef} className="text-2xl md:text-4xl text-gray-300 font-medium font-display mb-6">
                            <SplitText text="We help you" />
                            <span className="inline-block ml-3 align-baseline relative" style={{ minWidth: '6ch' }}>
                                <span ref={rotateRef} className="inline-block text-gradient-red">
                                    {rotatingWords[rotIdx]}
                                </span>
                            </span>
                        </div>

                        <p className="text-gray-300 max-w-2xl mb-8 text-lg leading-relaxed">
                            Indonesia's premier cybersecurity collective. Hands-on training labs,
                            elite red team engagements, and end-to-end Capture The Flag event
                            operations, built by practitioners, in Bahasa Indonesia, at student-friendly prices.
                        </p>

                        <div ref={ctaRef} className="flex flex-wrap gap-3 mb-10">
                            <Link to="services" smooth duration={600} offset={-40} className="cta-item">
                                <Magnetic>
                                    <span className="btn-primary cursor-pointer">
                                        Explore services <FiArrowRight />
                                    </span>
                                </Magnetic>
                            </Link>
                            <RouterLink to="/services/ctf-vendor" className="cta-item">
                                <Magnetic>
                                    <span className="btn-ghost">
                                        <FiZap /> CTF event vendor
                                    </span>
                                </Magnetic>
                            </RouterLink>
                            <Link to="contact" smooth duration={600} offset={-40} className="cta-item">
                                <Magnetic>
                                    <span className="btn-ghost cursor-pointer">Talk to us</span>
                                </Magnetic>
                            </Link>
                        </div>

                        <div ref={statsRef} className="grid grid-cols-3 gap-3 max-w-xl">
                            {[
                                { k: '700+', v: 'Students trained' },
                                { k: '15+', v: 'CTF events run' },
                                { k: '10+', v: 'Corporate clients' },
                            ].map((s) => (
                                <div key={s.v} className="stat card p-4">
                                    <div className="font-display text-2xl text-gradient-red font-bold">{s.k}</div>
                                    <div className="text-xs text-gray-400 mt-1 font-mono uppercase tracking-wider">{s.v}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Terminal */}
                    <div className="lg:col-span-5">
                        <div ref={termRef} className="card p-0 overflow-hidden font-mono text-sm shadow-2xl relative">
                            <div className="scan-beam" aria-hidden="true" />
                            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-white/[0.02]">
                                <div className="w-3 h-3 bg-red-500/80 rounded-full" />
                                <div className="w-3 h-3 bg-yellow-500/80 rounded-full" />
                                <div className="w-3 h-3 bg-green-500/80 rounded-full" />
                                <span className="ml-3 text-xs text-gray-400">siberlab@ops:~</span>
                                <span className="ml-auto flex items-center gap-1 text-xs text-emerald-400">
                                    <FiTerminal /> tun0 up
                                </span>
                            </div>
                            <div className="p-5 min-h-[280px] bg-[#06070b] relative">
                                {linesToShow.map((line, i) => (
                                    <p key={i} className={`${terminalLines[i].className}`}>
                                        {line}
                                    </p>
                                ))}
                                {currentLine < terminalLines.length && (
                                    <p className={`${terminalLines[currentLine].className} typewriter`}>
                                        {terminalLines[currentLine].text.slice(0, charIndex)}
                                    </p>
                                )}
                            </div>
                            <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 bg-white/[0.02] text-xs text-gray-400">
                                <span className="flex items-center gap-2"><FiShield className="text-red-400" /> 0-day responsible disclosure friendly</span>
                                <span className="hidden md:inline">CERT-ID partner</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="absolute left-1/2 -translate-x-1/2 bottom-6 text-xs text-gray-500 font-mono tracking-widest animate-bounce hidden md:block">
                    SCROLL ↓
                </div>
            </div>
        </section>
    );
};

export default Hero;
